import { useState, lazy } from 'react'
import type { ReactNode } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'

import { UpgradeAlert } from '@/components/UpgradeAlert'
import {
  faChevronRight,
  faFileArrowDown,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { cn } from '@/utils'
import { format } from 'date-fns'

import type { Plan, PaddleTransaction } from '~shared/types'

import { useModal } from '../../features/modal-manager/index.ts'
import { getTeamTransactions } from '../../firestore'
import { Button } from '../button'

import DashboardWrapper from './DashboardWrapper'

const CancelSubscriptionModal = lazy(
  () => import('../modal/CancelSubscriptionModal'),
)

export default function Billing() {
  const { teamId } = useParams()
  const { plan } = useOutletContext<{ plan: Plan | null }>()

  {
    /* FREE PLAN */
  }
  if (!plan || !plan.billingCycle)
    return (
      <DashboardWrapper title="Billing">
        <PlanFree
          teamId={teamId}
          plan={plan}
        />
      </DashboardWrapper>
    )

  {
    /* EVENT PLAN */
  }
  if (plan.billingCycle.interval === 'once')
    return (
      <DashboardWrapper title="Billing">
        <PlanEvent
          teamId={teamId}
          plan={plan}
        />
      </DashboardWrapper>
    )

  {
    /* SUBSCRIPTION PLAN */
  }
  return (
    <DashboardWrapper title="Billing">
      <PlanSubscription
        teamId={teamId}
        plan={plan}
      />
    </DashboardWrapper>
  )
}

/**
 * Plan limits use `-1` as the unlimited sentinel (see assertWithinLimit).
 */
const formatMemberLimit = (plan: Plan | null): string | number => {
  const members = plan?.limits?.members
  return members === -1 ? 'unlimited' : members || 1
}

const PlanFree = ({ teamId, plan }: { teamId?: string; plan: Plan | null }) => {
  return (
    <PlanWrapper
      teamId={teamId}
      imageUrl={plan?.imageUrl}
      planName={plan?.name}
    >
      <UpgradeAlert
        message="Upgrade to add more features and team members."
        className="mt-4"
      />
    </PlanWrapper>
  )
}

const PlanEvent = ({ teamId, plan }: { teamId?: string; plan: Plan }) => {
  return (
    <PlanWrapper
      teamId={teamId}
      imageUrl={plan.imageUrl}
      planName={`${plan.name} • ${plan.billingCycle?.daysActive} Day Event`}
    >
      {plan.billingCycle?.endsAt && (
        <p className="text-xs">
          Event ends: {format(plan.billingCycle.endsAt, 'd MMM, yyyy')}
        </p>
      )}
      {/* Plan limits */}
      <div className="flex flex-col gap-1 text-xs">
        <p>Your plan allows for {formatMemberLimit(plan)} team members.</p>
      </div>
    </PlanWrapper>
  )
}

const PlanSubscription = ({
  teamId,
  plan,
}: {
  teamId?: string
  plan: Plan
}) => {
  const [cancelSubscriptionModal, renderCancelSubscriptionModal] = useModal(
    CancelSubscriptionModal,
  )

  return (
    <PlanWrapper
      teamId={teamId}
      imageUrl={plan.imageUrl}
      planName={`${plan.name} plan`}
      actionButton={
        <Button
          className="bg-transparent font-normal! text-sm text-gray-600! h-[unset] self-start p-0!"
          text="Cancel Subscription"
          onClick={cancelSubscriptionModal.show}
          disabled={plan.scheduledChange?.action === 'cancel'}
          toolTip={
            plan.scheduledChange?.action === 'cancel'
              ? 'Your subscription is already scheduled for cancelation'
              : ''
          }
          showTooltipWhenDisabled
        />
      }
    >
      {/* Plan limits */}
      <div className="flex flex-col gap-2 text-xs">
        {/* Billing interval, or cancelled */}
        {plan.scheduledChange?.action === 'cancel' ? (
          <>
            <p className="px-1 text-[10px] font-bold text-gray-100 bg-gray-700 w-max rounded-xs uppercase">
              Cancelled
            </p>
            <p className="text-xs text-gray-300">
              Subscription expires:{' '}
              {format(plan.scheduledChange.effective_at, 'd MMM, yyyy')}
            </p>
          </>
        ) : (
          <p>
            Billed:{' '}
            <span className="capitalize">{plan.billingCycle?.interval}</span>
            ly.
            {plan.billingCycle?.endsAt && (
              <>
                {' '}
                Next payment date:{' '}
                {format(plan.billingCycle.endsAt, 'd MMM, yyyy')}.
              </>
            )}
          </p>
        )}
        <p className="">
          <span>
            Your plan allows for {formatMemberLimit(plan)} team members.
          </span>
          {plan?.limits?.members !== -1 && (
            <>
              {' '}
              <span>Want to add more?</span>{' '}
              <a
                href="mailto:hello@rundownstudio.app"
                className="underline hover:text-gray-300 cursor-pointer"
              >
                Email us here
              </a>
            </>
          )}
        </p>
      </div>

      {renderCancelSubscriptionModal({
        plan,
        teamId,
      })}
    </PlanWrapper>
  )
}

const PlanWrapper = ({
  teamId,
  imageUrl,
  planName,
  children,
  actionButton = <></>,
}: {
  teamId?: string
  imageUrl?: string
  planName?: string
  children?: ReactNode
  actionButton?: ReactNode
}) => {
  const [showTransactions, setShowTransactions] = useState(false)
  const [loading, setLoading] = useState(false)
  const [transactions, setTransactions] = useState<PaddleTransaction[]>()

  const handleGetTransactions = async () => {
    if (showTransactions) return setShowTransactions(false)
    setLoading(true)
    const { data } = await getTeamTransactions(teamId)
    setTransactions(data as PaddleTransaction[])
    setLoading(false)
    return setShowTransactions(true)
  }

  return (
    <>
      <div
        className={[
          'p-4 flex gap-4 bg-gray-100/5 border border-gray-800',
          loading && 'bg-animated from-gray-900 to-gray-800',
        ].join(' ')}
      >
        <img
          src={imageUrl}
          className="mt-1 w-12 h-12 aspect-square"
        />
        <div className="flex flex-col gap-1 justify-center">
          {/* Plan name */}
          <p className="font-bold">{planName}</p>

          {children}

          {/* Transactions link and list */}
          <button
            className="mt-2 flex items-center gap-1.5 text-left text-xs text-gray-400 hover:text-gray-200 transition-colors"
            onClick={() => void handleGetTransactions()}
          >
            <FontAwesomeIcon
              icon={faChevronRight}
              className={cn(
                'text-[8px] transition-transform',
                showTransactions && 'rotate-90',
              )}
            />
            <span className="underline">
              {showTransactions ? 'Hide transactions' : 'View transactions'}
            </span>
          </button>

          {showTransactions &&
            (transactions?.length ? (
              <div className="mt-3 flex flex-col divide-y divide-gray-800 border-y border-gray-800 text-xs">
                {transactions.map((t) => (
                  <Transaction
                    key={t.id}
                    transaction={t}
                  />
                ))}
              </div>
            ) : (
              <p className="mt-3 text-xs text-gray-500">No transactions yet.</p>
            ))}
        </div>
        <span className="flex-1"></span>
        {actionButton}
      </div>
    </>
  )
}

const billingApiBase = import.meta.env.VITE_BILLING_FUNCTION_BASE

const Transaction = ({ transaction }: { transaction: PaddleTransaction }) => {
  const { grand_total, currency_code } = transaction.details.totals

  return (
    <div className="group grid grid-cols-[7.5rem_1fr_auto] items-center gap-4 py-1.5">
      <p className="font-medium text-gray-300">
        {transaction.billed_at
          ? format(new Date(transaction.billed_at), 'd MMM, yyyy')
          : '-'}
      </p>
      <p className="text-right tabular-nums text-gray-100">
        {(grand_total / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: currency_code,
        })}
      </p>
      <a
        href={`${billingApiBase}/transactions/${transaction.id}/invoice`}
        className="flex items-center gap-1.5 text-gray-500 group-hover:text-gray-200 transition-colors"
        title="Download invoice"
      >
        <FontAwesomeIcon icon={faFileArrowDown} />
        <span className="sr-only">Download invoice</span>
      </a>
    </div>
  )
}
